/**
 * Backend store for managing the application's backend API instance.
 * Provides centralized access to the backend API throughout the application.
 * Uses Zustand for state management and integrates with the application's
 * configuration system for API endpoint management.
 */

'use client';

import { create } from 'zustand';
import { useEffect } from 'react';
import { useConfigStore } from './configStore';
import { BackendApi, IBackend } from '@/api/BackendApi';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';
import { CsrfTokenProvider } from '@/api/AuthorizedApiBase';
import { normalizeBackendUrl } from '@/lib/utils/urlUtils';

/**
 * Backend state interface.
 * Defines the state and actions available in the backend store.
 */
interface BackendState {
  /** The backend API instance */
  backend: IBackend | null;
  /** Flag indicating if the store has been initialized */
  isInitialized: boolean;
  /** Current error message if any */
  error: string | null;
  /**
   * Initializes the backend with the provided API endpoint.
   * @param apiEndpoint - The base URL for the backend API
   */
  initializeBackend: (apiEndpoint: string) => void;
  /**
   * Gets the current backend instance.
   * @throws Error if backend is not initialized
   */
  getBackend: () => IBackend;
}

/**
 * Creates and manages the backend store using Zustand.
 * The store maintains the backend API instance and its initialization state.
 * @returns A Zustand store with backend state management capabilities
 */
export const useBackendStore = create<BackendState>()((set, get) => ({
  backend: null,
  isInitialized: false,
  error: null,

  initializeBackend: (apiEndpoint: string) => {
    try {
      if (get().backend) return;

      const baseUrlNoTrailingSlash = normalizeBackendUrl(apiEndpoint);
      const newBackend = BackendApi(CsrfTokenProvider, baseUrlNoTrailingSlash);
      log.debug('[BackendStore] Created new backend instance:', newBackend);

      set({
        backend: newBackend,
        isInitialized: true,
        error: null,
      });
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to initialize backend');
      log.error('[BackendStore] Backend initialization failed:', errorMessage);
      set({ error: errorMessage });
    }
  },

  getBackend: () => {
    const { backend, error } = get();

    if (error) {
      throw new Error(error);
    }

    if (!backend) {
      throw new Error('Backend not initialized');
    }

    return backend;
  },
}));

/**
 * Effect hook to initialize the backend when the config is available.
 * This should be used at the application root level.
 *
 * @example
 * ```tsx
 * function App() {
 *   useInitializeBackend();
 *   return <YourApp />;
 * }
 * ```
 *
 * @remarks
 * - Automatically initializes the backend when the system config is loaded
 * - Handles dependency tracking for config changes
 * - No-op if API endpoint is not available in config
 */
export function useInitializeBackend() {
  const configStore = useConfigStore();
  const initializeBackend = useBackendStore((state) => state.initializeBackend);

  useEffect(() => {
    if (!configStore.systemConfig?.apiEndpoint) {
      return;
    }

    initializeBackend(configStore.systemConfig.apiEndpoint);
  }, [configStore.systemConfig?.apiEndpoint, initializeBackend]);
}
